/**
 * useGameLeaderboard — fetch & publish game high scores via Nostr
 *
 * Score events are kind 30078 (NIP-78 application-specific data) with:
 *   d-tag:  "bchnostr/game-score/<gameId>"   (one per player per game)
 *   content:  JSON  { score: number, level?: number, ... }
 *   tags:  [["d", "bchnostr/game-score/<gameId>"], ["t", "bchnostr-game"]]
 *
 * Because 30078 is parameterized-replaceable (kind 30000-39999), each
 * author can only have ONE score event per game — publishing a new one
 * replaces the old one.  The leaderboard queries all these events and
 * ranks them by score descending.
 */

import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { type Event } from 'nostr-tools';
import { useNostrQuery } from './useNostrQuery';
import { useNostrPublish } from './useNostrPublish';

const SCORE_KIND = 30078;
const GAME_TAG = 'bchnostr-game';

function dTag(gameId: string) {
  return `bchnostr/game-score/${gameId}`;
}

export interface ScoreEntry {
  pubkey: string;
  score: number;
  level?: number;
  /** Extra game-specific data (e.g. Wordle stats) */
  meta?: Record<string, any>;
  timestamp: number;
  eventId: string;
}

export interface UseGameLeaderboardResult {
  /** Sorted descending by score */
  scores: ScoreEntry[];
  loading: boolean;
  /** Submit a new high score. Only publishes if it beats the player's current score. */
  submitScore: (score: number, extra?: { level?: number; meta?: Record<string, any> }) => Promise<boolean>;
  /** Current user's best score on the board, or null */
  myScore: ScoreEntry | null;
  /** Refetch from relays */
  refetch: () => void;
}

export function useGameLeaderboard(gameId: string, userPubkey?: string): UseGameLeaderboardResult {
  const { publish } = useNostrPublish();
  const dtag = dTag(gameId);

  // Stable filter
  const filters = useMemo(() => [
    { kinds: [SCORE_KIND], '#d': [dtag], '#t': [GAME_TAG], limit: 200 },
  ], [dtag]);

  const { events, loading, refetch } = useNostrQuery({
    filters,
    enabled: true,
    allRelays: true,
  });

  // Parse events into ScoreEntry[], keeping only the newest per pubkey
  // (useNostrQuery already deduplicates replaceable events, but
  // just in case we double-check)
  const scores = useMemo(() => {
    const map = new Map<string, ScoreEntry>();

    for (const ev of events) {
      // Only keep the newest event per pubkey
      const existing = map.get(ev.pubkey);
      if (existing && existing.timestamp >= ev.created_at) continue;

      try {
        const data = JSON.parse(ev.content);
        if (typeof data.score !== 'number' || data.score <= 0) continue;

        map.set(ev.pubkey, {
          pubkey: ev.pubkey,
          score: data.score,
          level: data.level,
          meta: data.meta,
          timestamp: ev.created_at,
          eventId: ev.id,
        });
      } catch {
        // Invalid JSON — skip
      }
    }

    return [...map.values()].sort((a, b) => b.score - a.score);
  }, [events]);

  const myScore = useMemo(() => {
    if (!userPubkey) return null;
    return scores.find(s => s.pubkey === userPubkey) || null;
  }, [scores, userPubkey]);

  // Track the last submitted score to avoid redundant publishes within a session
  const lastSubmittedRef = useRef<number>(0);

  const submitScore = useCallback(async (
    score: number,
    extra?: { level?: number; meta?: Record<string, any> },
  ): Promise<boolean> => {
    if (!userPubkey || score <= 0) return false;

    // Only publish if this beats the player's current best
    const currentBest = myScore?.score ?? 0;
    if (score <= currentBest) return false;

    // Avoid redundant publishes within the same session
    if (score <= lastSubmittedRef.current) return false;
    lastSubmittedRef.current = score;

    try {
      const content = JSON.stringify({
        score,
        ...(extra?.level != null ? { level: extra.level } : {}),
        ...(extra?.meta ? { meta: extra.meta } : {}),
      });

      await publish({
        kind: SCORE_KIND,
        content,
        tags: [
          ['d', dtag],
          ['t', GAME_TAG],
          ['t', `bchnostr-${gameId}`],
        ],
        created_at: Math.floor(Date.now() / 1000),
      });

      return true;
    } catch {
      return false;
    }
  }, [userPubkey, myScore, publish, dtag, gameId]);

  return { scores, loading, submitScore, myScore, refetch };
}

